<script>
  import { _ } from "svelte-i18n";
  import Header from "lib/components/Header.svelte";
  import Footer from "lib/components/Footer.svelte";
  import Container from "lib/components/Container.svelte";
  import Card from "lib/components/Card.svelte";
  import Grid from "lib/components/Grid.svelte";
  import Button from "lib/components/Button.svelte";
  import { onMount } from "svelte";
  import { APP_URL } from "utils/menu.js";

  const registerUrl = `${APP_URL}?signup=true`;

  let container;
  let scriptLoaded = false;
  let widget;
  let selectedMetal = "gold"; // 'gold' or 'silver'
  let selectedRange = "1M";

  const metals = [
    {
      id: "gold",
      label: "Gold",
      symbol: "OANDA:XAUAUD",
      instrumentId: "CRNMUxdNcHJpphgzimWkbihLWRP2XAM7aczR",
    },
    {
      id: "silver",
      label: "Silver",
      symbol: "OANDA:XAGAUD",
      instrumentId: "CRN8nJcjFQueVZDZujGR61YkuRMwHnKcbxgd",
    },
  ];

  let gramPriceBuy = null;
  let gramPriceSell = null;
  let priceLoading = false;
  let priceError = false;
  let isBusinessHours = true;
  let outOfHoursMessage = "";

  const dateRanges = [
    { label: "1M", value: "1M", interval: "D" },
    { label: "3M", value: "3M", interval: "D" },
    { label: "6M", value: "6M", interval: "D" },
    { label: "1Y", value: "12M", interval: "D" },
    { label: "5Y", value: "60M", interval: "W" },
    { label: "All", value: "ALL", interval: "W" },
  ];

  // Pricing features
  const pricingFeatures = [
    {
      icon: "📊",
      titleKey: "pricing.features.realtime.title",
      descriptionKey: "pricing.features.realtime.description",
    },
    {
      icon: "💰",
      titleKey: "pricing.features.transparent.title",
      descriptionKey: "pricing.features.transparent.description",
      linkKey: "pricing.features.transparent.feesLink",
    },
    {
      icon: "🌐",
      titleKey: "pricing.features.global.title",
      descriptionKey: "pricing.features.global.description",
    },
  ];

  // Reactive variables that automatically update when language changes
  $: selectedMetalData = metals.find((m) => m.id === selectedMetal);
  $: formattedGramBuy =
    gramPriceBuy !== null ? `$${Number(gramPriceBuy).toFixed(4)}` : null;
  $: formattedGramSell =
    gramPriceSell !== null ? `$${Number(gramPriceSell).toFixed(4)}` : null;
  $: formattedTroyBuy =
    gramPriceBuy !== null
      ? `$${(Number(gramPriceBuy) * 31.1035).toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
      : null;
  $: formattedTroySell =
    gramPriceSell !== null
      ? `$${(Number(gramPriceSell) * 31.1035).toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
      : null;

  $: subtitleWithLink = (() => {
    const subtitle = $_("pricing.subtitle");
    const linkText = $_("pricing.subtitle.feesLink");
    return subtitle.replace(
      "{feesLink}",
      `<a href="/docs/fees">${linkText}</a>`,
    );
  })();

  $: ctaTextWithLink = (() => {
    const ctaText = $_("pricing.cta.text", {
      default:
        "Register now to purchase precious metals at close-to-spot prices with transparent fees.",
    });
    const linkText = $_("pricing.cta.feesLink", {
      default: "detailed fee structure",
    });
    return ctaText.replace(
      "{feesLink}",
      `<a href="/docs/fees">${linkText}</a>`,
    );
  })();

  // Reactive variable for feature descriptions with links
  $: featureDescriptions = pricingFeatures.map((feature) => {
    const description = $_(feature.descriptionKey, { default: "Description" });
    if (feature.linkKey) {
      const linkText = $_(feature.linkKey, { default: "link" });
      return description.replace(
        "{feesLink}",
        `<a href="/docs/fees">${linkText}</a>`,
      );
    }
    return description;
  });

  function getBusinessHoursInfo() {
    const now = new Date();
    const parts = new Intl.DateTimeFormat("en-AU", {
      timeZone: "Australia/Sydney",
      weekday: "long",
      hour: "numeric",
      hour12: false,
    }).formatToParts(now);
    const weekday = parts.find((p) => p.type === "weekday").value;
    const hour = parseInt(parts.find((p) => p.type === "hour").value, 10);
    const isWeekend = weekday === "Saturday" || weekday === "Sunday";
    const inHours = !isWeekend && hour >= 9 && hour < 17;

    let nextOpen = "";
    if (isWeekend) {
      nextOpen = "Our best rates return on Monday at 9 AM AEST.";
    } else if (hour < 9) {
      nextOpen = "Better rates open today at 9 AM AEST.";
    } else {
      nextOpen = "Better rates return on the next business day at 9 AM AEST.";
    }

    return { inHours, nextOpen };
  }

  onMount(() => {
    const { inHours, nextOpen } = getBusinessHoursInfo();
    isBusinessHours = inHours;
    outOfHoursMessage = nextOpen;

    const script = document.createElement("script");
    script.src = "https://s3.tradingview.com/tv.js";
    script.async = true;
    script.onload = () => {
      scriptLoaded = true;
      initWidget();
    };
    document.head.appendChild(script);
    fetchGramPrice(selectedMetal);

    return () => {
      if (container) {
        container.innerHTML = "";
      }
    };
  });

  function initWidget() {
    if (typeof TradingView !== "undefined" && container) {
      container.innerHTML = "";

      const metalData = metals.find((m) => m.id === selectedMetal);
      const rangeConfig = dateRanges.find((r) => r.value === selectedRange);
      const interval = rangeConfig ? rangeConfig.interval : "D";

      widget = new TradingView.widget({
        autosize: true,
        symbol: metalData.symbol,
        interval: interval,
        timezone: "Australia/Sydney",
        theme: "dark",
        style: "2",
        locale: "en",
        toolbar_bg: "#1a1a1a",
        enable_publishing: false,
        hide_top_toolbar: true,
        hide_side_toolbar: true,
        hide_legend: true,
        hide_volume: true,
        allow_symbol_change: false,
        withdateranges: false,
        save_image: false,
        container_id: container.id,
        height: 450,
        width: "100%",
        studies: [],
        range: selectedRange,
        disabled_features: [
          "header_symbol_search",
          "symbol_search_hot_key",
          "header_compare",
          "header_undo_redo",
          "header_screenshot",
          "header_saveload",
          "go_to_date",
          "context_menus",
          "scales_context_menu",
          "legend_context_menu",
          "property_pages",
          "show_chart_property_page",
          "source_selection_markers",
          "display_market_status",
          "chart_scroll",
          "chart_zoom",
        ],
        enabled_features: ["hide_left_toolbar_by_default"],
        backgroundColor: "#1a1a1a",
        overrides: {
          "mainSeriesProperties.lineStyle.color": "#d2a552",
          "mainSeriesProperties.lineStyle.linewidth": 3,
          "mainSeriesProperties.areaStyle.color1": "rgba(210, 165, 82, 0.3)",
          "mainSeriesProperties.areaStyle.color2": "rgba(210, 165, 82, 0.02)",
          "mainSeriesProperties.areaStyle.linecolor": "#d2a552",
          "mainSeriesProperties.areaStyle.linestyle": 0,
          "mainSeriesProperties.areaStyle.linewidth": 3,
          "mainSeriesProperties.areaStyle.priceSource": "close",
          "paneProperties.background": "#1a1a1a",
          "paneProperties.backgroundType": "solid",
          "paneProperties.vertGridProperties.color": "rgba(210, 165, 82, 0.04)",
          "paneProperties.horzGridProperties.color": "rgba(210, 165, 82, 0.04)",
          "scalesProperties.textColor": "#999999",
          "scalesProperties.lineColor": "rgba(210, 165, 82, 0.1)",
        },
      });
    }
  }

  function changeRange(range) {
    selectedRange = range;
    initWidget();
  }

  async function fetchGramPrice(metalId) {
    const metal = metals.find((m) => m.id === metalId);
    priceLoading = true;
    priceError = false;
    gramPriceBuy = null;
    gramPriceSell = null;
    try {
      const response = await fetch(
        `https://api.liquidnoble.com/v1/banking/instrument/price/?instrument_id=${metal.instrumentId}&pay_with=AUD`,
      );
      if (!response.ok) {
        throw new Error("Price fetch failed");
      }
      const data = await response.json();
      gramPriceBuy = data.AUD.unit_price_buy;
      gramPriceSell = data.AUD.unit_price_sell;
    } catch (error) {
      priceError = true;
    } finally {
      priceLoading = false;
    }
  }

  function changeMetal(metalId) {
    selectedMetal = metalId;
    fetchGramPrice(metalId);
    initWidget();
  }
</script>

<Header />

<Container class="pricing-page">
  <!-- Hero Section -->
  <div class="pricing-hero">
    <h1 class="page-title">
      {$_("pricing.title", { default: "Live Precious Metal Prices" })}
    </h1>
    <p class="page-subtitle">
      {@html subtitleWithLink}
    </p>
  </div>

  <!-- Main Chart Section -->
  <div class="chart-section">
    <div class="chart-card">
      <div class="chart-wrapper">
        <!-- Metal Selector and Date Range Controls -->
        <div class="controls-container">
          <div class="metal-selector-group">
            <div class="metal-selector">
              {#each metals as metal}
                <button
                  class="control-button"
                  class:active={selectedMetal === metal.id}
                  on:click={() => changeMetal(metal.id)}
                >
                  {metal.label}
                </button>
              {/each}
            </div>
            <div class="unit-display">
              <span class="unit-label">Price per</span>
              <span class="unit-value">troy oz</span>
              <span class="currency-badge">AUD</span>
            </div>
          </div>

          <div class="date-range-controls">
            {#each dateRanges as range}
              <button
                class="control-button"
                class:active={selectedRange === range.value}
                on:click={() => changeRange(range.value)}
              >
                {range.label}
              </button>
            {/each}
          </div>
        </div>

        <!-- Chart -->
        <div class="tradingview-wrapper">
          <div
            bind:this={container}
            id="tradingview_chart"
            class="tradingview-container"
          />
          {#if !scriptLoaded}
            <div class="loading">Loading chart...</div>
          {/if}
        </div>
        <p class="chart-note">
          {$_("pricing.chart.note", {
            default:
              "Chart data provided by TradingView. Prices update in real-time during market hours.",
          })}
        </p>
        <p class="chart-note unit-conversion">1 troy ounce = 31.1035 grams</p>
      </div>
    </div>
  </div>

  <!-- Liquid Noble Price Table -->
  {#if !priceError}
    <div class="lnprice-section">
      {#if !isBusinessHours}
        <div class="out-of-hours-notice">
          <span class="notice-icon">🕐</span>
          <div class="notice-body">
            <strong>You're currently seeing out-of-hours pricing</strong>
            <p>
              Prices at this time include a small premium - we trade closest to
              spot during business hours. {outOfHoursMessage}
            </p>
          </div>
        </div>
      {/if}

      <div class="lnprice-card">
        <div class="lnprice-heading">
          <div class="lnprice-heading-text">
            <h2 class="lnprice-title">
              {selectedMetalData.label} - Liquid Noble Prices
            </h2>
            <p class="lnprice-subtitle">
              All prices are in AUD and include all applicable fees. No hidden
              charges.
            </p>
          </div>
          <div class="lnprice-hours-badge">
            <span class="hours-badge-label">Best rates</span>
            <span class="hours-badge-value">Mon-Fri · 9 AM-5 PM AEST</span>
          </div>
        </div>

        {#if priceLoading}
          <div class="lnprice-loading">Fetching current prices...</div>
        {:else if gramPriceBuy !== null}
          <div class="lnprice-table">
            <div class="lnprice-table-row lnprice-table-header">
              <div class="lnprice-table-cell"></div>
              <div class="lnprice-table-cell lnprice-col-label">Per gram</div>
              <div class="lnprice-table-cell lnprice-col-label">
                Per troy oz
              </div>
            </div>
            <div class="lnprice-table-row">
              <div class="lnprice-table-cell lnprice-row-label buy-label">
                Buy
              </div>
              <div class="lnprice-table-cell lnprice-price">
                {formattedGramBuy}
              </div>
              <div class="lnprice-table-cell lnprice-price">
                {formattedTroyBuy}
              </div>
            </div>
            <div class="lnprice-table-row">
              <div class="lnprice-table-cell lnprice-row-label sell-label">
                Sell
              </div>
              <div class="lnprice-table-cell lnprice-price">
                {formattedGramSell}
              </div>
              <div class="lnprice-table-cell lnprice-price">
                {formattedTroySell}
              </div>
            </div>
          </div>
        {/if}
      </div>
    </div>
  {/if}

  <!-- Pricing Features -->
  <div class="features-section">
    <Grid columns="3" gap="1.5rem">
      {#each pricingFeatures as feature, index}
        <Card variant="dark" alignment="center">
          <div class="pricing-feature-icon" slot="icon">{feature.icon}</div>
          <h3 slot="title">
            {$_(feature.titleKey, { default: "Feature" })}
          </h3>
          <div slot="description">
            {@html featureDescriptions[index]}
          </div>
        </Card>
      {/each}
    </Grid>
  </div>

  <!-- CTA Section -->
  <div class="cta-section">
    <Card variant="glass" alignment="center">
      <h2 slot="title">
        {$_("pricing.cta.title", { default: "Ready to Start?" })}
      </h2>
      <div slot="description" class="cta-content">
        <p class="cta-text">
          {@html ctaTextWithLink}
        </p>
        <Button href={registerUrl} variant="primary" size="large">
          {$_("pricing.cta.button", { default: "Register Now" })}
        </Button>
      </div>
    </Card>
  </div>
</Container>

<Footer />

<style lang="scss">
  .pricing-page {
    min-height: 100vh;
  }

  /* Hero Section - More Compact */
  .pricing-hero {
    text-align: center;
    max-width: 900px;
    margin: 0 auto 2rem;
    padding-top: 1rem;
  }

  .page-title {
    font-size: 2.5rem;
    font-weight: 800;
    margin-bottom: 1rem;
    color: var(--text-color1);
    line-height: 1.1;
    background: linear-gradient(135deg, #f6efd3 0%, #d2a552 50%, #dbbb6b 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  .page-subtitle {
    font-size: 1.1rem;
    line-height: 1.5;
    color: var(--text-color2);
    font-weight: 400;
  }

  /* Chart Section */
  .chart-section {
    margin-bottom: 2.5rem;
  }

  .chart-card {
    background: rgba(42, 32, 52, 0.6);
    backdrop-filter: blur(10px);
    border: 1px solid rgba(210, 165, 82, 0.2);
    border-radius: 16px;
    padding: 2rem;
    transition: none;
  }

  .chart-wrapper {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  /* Controls Container */
  .controls-container {
    display: flex;
    gap: 16px;
    align-items: center;
    flex-wrap: wrap;
    justify-content: space-between;
  }

  .metal-selector-group {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
  }

  .metal-selector,
  .date-range-controls {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
  }

  .unit-display {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 8px 16px;
    background: rgba(210, 165, 82, 0.08);
    border: 1px solid rgba(210, 165, 82, 0.2);
    border-radius: 8px;
  }

  .unit-label {
    font-size: 13px;
    color: rgba(210, 165, 82, 0.7);
    font-weight: 500;
  }

  .unit-value {
    font-size: 14px;
    color: #d2a552;
    font-weight: 600;
  }

  .currency-badge {
    font-size: 11px;
    color: rgba(210, 165, 82, 0.9);
    font-weight: 700;
    letter-spacing: 0.06em;
    background: rgba(210, 165, 82, 0.15);
    border: 1px solid rgba(210, 165, 82, 0.3);
    border-radius: 4px;
    padding: 1px 5px;
    margin-left: 2px;
  }

  .lnprice-section {
    margin-bottom: 2.5rem;
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .out-of-hours-notice {
    display: flex;
    align-items: flex-start;
    gap: 14px;
    padding: 16px 20px;
    background: rgba(255, 152, 0, 0.1);
    border: 1px solid rgba(255, 152, 0, 0.45);
    border-radius: 12px;
  }

  .notice-icon {
    font-size: 1.4rem;
    flex-shrink: 0;
    line-height: 1.4;
  }

  .notice-body strong {
    display: block;
    font-size: 0.95rem;
    font-weight: 700;
    margin-bottom: 5px;
    color: #ffb300;
  }

  .notice-body p {
    margin: 0;
    font-size: 0.875rem;
    line-height: 1.5;
    color: rgba(255, 179, 0, 0.85);
  }

  .lnprice-card {
    background: rgba(42, 32, 52, 0.6);
    backdrop-filter: blur(10px);
    border: 1px solid rgba(210, 165, 82, 0.2);
    border-radius: 16px;
    padding: 2rem;
  }

  .lnprice-heading {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1rem;
    margin-bottom: 1.5rem;
    flex-wrap: wrap;
  }

  .lnprice-title {
    font-size: 1.25rem;
    font-weight: 700;
    color: var(--text-color1);
    margin: 0 0 0.35rem;
  }

  .lnprice-subtitle {
    margin: 0;
    font-size: 0.875rem;
    color: var(--text-color2);
    line-height: 1.4;
  }

  .lnprice-hours-badge {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 2px;
    flex-shrink: 0;
  }

  .hours-badge-label {
    font-size: 0.7rem;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: rgba(210, 165, 82, 0.55);
    font-weight: 600;
  }

  .hours-badge-value {
    font-size: 0.85rem;
    color: #d2a552;
    font-weight: 600;
  }

  .lnprice-loading {
    text-align: center;
    color: rgba(210, 165, 82, 0.5);
    font-style: italic;
    font-size: 0.9rem;
    padding: 1rem 0;
  }

  .lnprice-table {
    border: 1px solid rgba(210, 165, 82, 0.15);
    border-radius: 10px;
    overflow: hidden;
  }

  .lnprice-table-row {
    display: grid;
    grid-template-columns: 80px 1fr 1fr;
    border-bottom: 1px solid rgba(210, 165, 82, 0.1);
  }

  .lnprice-table-row:last-child {
    border-bottom: none;
  }

  .lnprice-table-header {
    background: rgba(210, 165, 82, 0.06);
  }

  .lnprice-table-cell {
    padding: 12px 16px;
    display: flex;
    align-items: center;
  }

  .lnprice-col-label {
    font-size: 0.75rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.07em;
    color: rgba(210, 165, 82, 0.6);
  }

  .lnprice-row-label {
    font-size: 0.8rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.08em;
  }

  .buy-label {
    color: #00c853;
  }

  .sell-label {
    color: #ff5252;
  }

  .lnprice-price {
    font-size: 1.05rem;
    font-weight: 600;
    color: var(--text-color1);
    font-variant-numeric: tabular-nums;
  }

  .control-button {
    background: rgba(26, 26, 26, 0.5);
    backdrop-filter: blur(10px);
    border: 1px solid rgba(210, 165, 82, 0.2);
    border-radius: 8px;
    color: #d2a552;
    padding: 8px 20px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    font-family: inherit;
  }

  .control-button:hover {
    background: rgba(210, 165, 82, 0.1);
    border-color: rgba(210, 165, 82, 0.4);
    transform: translateY(-1px);
  }

  .control-button.active {
    background: #d2a552;
    color: #1a1a1a;
    border-color: #d2a552;
    font-weight: 600;
  }

  .control-button:active {
    transform: translateY(0);
  }

  /* Chart */
  .tradingview-wrapper {
    position: relative;
    border-radius: 12px;
    overflow: hidden;
    background: rgba(26, 26, 26, 0.5);
    backdrop-filter: blur(10px);
    border: 1px solid rgba(210, 165, 82, 0.2);
    height: 450px;
  }

  .tradingview-container {
    width: 100%;
    height: 100%;
  }

  .loading {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    color: #d2a552;
    font-size: 16px;
    font-weight: 500;
  }

  .chart-note {
    margin: 0;
    font-size: 0.85rem;
    color: #999;
    text-align: center;
    font-style: italic;
  }

  .chart-note.unit-conversion {
    margin-top: 0.25rem;
    font-size: 0.8rem;
    color: rgba(210, 165, 82, 0.6);
    font-style: normal;
  }

  /* Sections */
  .features-section {
    margin-bottom: 2.5rem;
  }

  /* Feature Cards */
  :global(.pricing-feature-icon) {
    font-size: 2.5rem;
    margin-bottom: 1rem;
    transition: all 0.3s ease;
    filter: saturate(0.7) brightness(1.1);
  }

  :global(.card:hover .pricing-feature-icon) {
    filter: none;
    transform: scale(1.1);
  }

  /* CTA Section */
  .cta-section {
    margin-bottom: 3rem;
  }

  .cta-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1.5rem;
  }

  .cta-text {
    font-size: 1.1rem;
    color: var(--text-color2);
    max-width: 600px;
    margin: 0;
    line-height: 1.6;
  }

  /* Link Styling */
  .page-subtitle :global(a),
  .cta-text :global(a),
  :global(.card a) {
    color: #d2a552;
    text-decoration: none;
    border-bottom: 1px solid rgba(210, 165, 82, 0.3);
    transition: all 0.2s ease;
  }

  .page-subtitle :global(a:hover),
  .cta-text :global(a:hover),
  :global(.card a:hover) {
    color: #dbbb6b;
    border-bottom-color: #dbbb6b;
  }

  /* Responsive Design */
  @media (max-width: 1024px) {
    .page-title {
      font-size: 2.25rem;
    }
  }

  @media (max-width: 768px) {
    :global(.features-section .grid) {
      grid-template-columns: 1fr !important;
    }

    .pricing-hero {
      margin-bottom: 1.5rem;
    }

    .page-title {
      font-size: 2rem;
    }

    .page-subtitle {
      font-size: 1rem;
    }

    .chart-section,
    .features-section {
      margin-bottom: 2rem;
    }

    .controls-container {
      flex-direction: column;
      align-items: stretch;
      gap: 12px;
    }

    .metal-selector-group {
      flex-direction: column;
      align-items: stretch;
    }

    .metal-selector,
    .date-range-controls {
      justify-content: center;
    }

    .unit-display {
      justify-content: center;
    }
  }

  @media (max-width: 640px) {
    .control-button {
      flex: 1;
      min-width: 60px;
      padding: 8px 12px;
      font-size: 13px;
    }

    .tradingview-wrapper {
      height: 400px;
    }

    .chart-card {
      padding: 1.5rem;
    }
  }

  @media (max-width: 480px) {
    .page-title {
      font-size: 1.75rem;
    }

    .cta-text {
      font-size: 1rem;
    }

    .tradingview-wrapper {
      height: 350px;
    }

    .chart-card {
      padding: 1.25rem;
    }
  }
</style>
